<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'
import Header from './component/header.vue'

const store      = useStore()
const permission = computed(() => store.state.getCurrentUserPermission)

const loading   = ref(false)
const saving    = ref(false)
const goals     = ref({ data: [], current_page: 1, last_page: 1, total: 0 })
const cycles    = ref([])
const employees = ref([])
const cycleFilter = ref('')
const statusFilter = ref('')
const page      = ref(1)
const showModal = ref(false)
const editId    = ref(null)
const formError = ref('')

const blankForm = () => ({
  cycle_id: '', employee_id: '', title: '', description: '',
  weight: '', target_value: '', unit: '', due_date: '', status: 'draft',
})
const form = ref(blankForm())

const load = async () => {
  try {
    loading.value = true
    const params = { per_page: 20, page: page.value }
    if (cycleFilter.value)  params.cycle_id = cycleFilter.value
    if (statusFilter.value) params.status   = statusFilter.value
    const res = await axios.get('/hr/pms/goals', { params })
    goals.value = res.data.data
  } catch (e) { console.error(e) } finally { loading.value = false }
}

const loadMeta = async () => {
  try {
    const [c, e] = await Promise.all([
      axios.get('/hr/pms/cycles'),
      axios.get('/payroll/employees/active'),
    ])
    cycles.value    = c.data.data?.data ?? c.data.data ?? []
    employees.value = e.data.data ?? []
  } catch (e) { console.error(e) }
}

watch([cycleFilter, statusFilter], () => { page.value = 1; load() })
watch(page, load)
onMounted(() => { load(); loadMeta() })

const openCreate = () => {
  editId.value = null
  form.value = blankForm()
  formError.value = ''
  showModal.value = true
}

const openEdit = (g) => {
  editId.value = g.id
  form.value = {
    cycle_id: g.cycle_id, employee_id: g.employee_id,
    title: g.title, description: g.description ?? '',
    weight: g.weight ?? '', target_value: g.target_value ?? '',
    unit: g.unit ?? '', due_date: g.due_date ?? '', status: g.status,
  }
  formError.value = ''
  showModal.value = true
}

const save = async () => {
  if (!form.value.cycle_id || !form.value.employee_id || !form.value.title) {
    formError.value = 'Cycle, employee and title are required.'
    return
  }
  try {
    saving.value = true; formError.value = ''
    if (editId.value) {
      await axios.put(`/hr/pms/goals/${editId.value}`, form.value)
    } else {
      await axios.post('/hr/pms/goals', form.value)
    }
    showModal.value = false
    load()
  } catch (e) {
    formError.value = e.response?.data?.message ?? 'Failed to save'
  } finally { saving.value = false }
}

const approve = async (id) => {
  if (!confirm('Approve this goal?')) return
  await axios.post(`/hr/pms/goals/${id}/approve`)
  load()
}

const deleteGoal = async (id) => {
  if (!confirm('Delete this goal?')) return
  await axios.delete(`/hr/pms/goals/${id}`)
  load()
}

const statusClass = (s) => ({
  draft: 'badge--grey', approved: 'badge--green',
  in_progress: 'badge--blue', completed: 'badge--green', rejected: 'badge--red',
}[s] ?? '')
</script>

<template>
  <div>
    <Header />
    <div class="page">
      <div class="toolbar">
        <h2 class="page-title">Goals</h2>
        <button v-if="permission?.manage_pms_goals" class="btn-primary" @click="openCreate">+ New Goal</button>
      </div>

      <div class="filters">
        <select v-model="cycleFilter" class="filter-select">
          <option value="">All Cycles</option>
          <option v-for="c in cycles" :key="c.id" :value="c.id">{{ c.title }}</option>
        </select>
        <select v-model="statusFilter" class="filter-select">
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="approved">Approved</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <div class="table-card">
        <div class="table-scroll">
          <table class="data-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Goal</th>
                <th>Cycle</th>
                <th>Weight</th>
                <th>Target</th>
                <th>Actual</th>
                <th>Due</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading"><td colspan="9" class="tbl-empty">Loading…</td></tr>
              <tr v-else-if="!goals.data?.length"><td colspan="9" class="tbl-empty">No goals found</td></tr>
              <tr v-for="g in goals.data" :key="g.id">
                <td>{{ g.employee?.full_name ?? '—' }}</td>
                <td>
                  <div class="goal-title">{{ g.title }}</div>
                  <div v-if="g.description" class="goal-desc">{{ g.description }}</div>
                </td>
                <td>{{ g.cycle?.title ?? '—' }}</td>
                <td>{{ g.weight ?? '—' }}%</td>
                <td>{{ g.target_value ?? '—' }} {{ g.unit }}</td>
                <td>{{ g.actual_value ?? '—' }}</td>
                <td>{{ g.due_date ?? '—' }}</td>
                <td><span :class="['badge', statusClass(g.status)]">{{ g.status }}</span></td>
                <td class="actions-cell">
                  <button v-if="permission?.approve_pms_goals && g.status === 'draft'" class="btn-link" @click="approve(g.id)">Approve</button>
                  <button v-if="permission?.manage_pms_goals" class="btn-link" @click="openEdit(g)">Edit</button>
                  <button v-if="permission?.manage_pms_goals && g.status === 'draft'" class="btn-link btn-link--red" @click="deleteGoal(g.id)">Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="goals.last_page > 1" class="pagination">
          <button :disabled="page <= 1" @click="page--">‹</button>
          <span>{{ page }} / {{ goals.last_page }}</span>
          <button :disabled="page >= goals.last_page" @click="page++">›</button>
        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h3>{{ editId ? 'Edit' : 'New' }} Goal</h3>
          <button class="modal-close" @click="showModal = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="formError" class="form-error">{{ formError }}</div>
          <div class="form-grid">
            <div class="form-group">
              <label>Cycle *</label>
              <select v-model="form.cycle_id">
                <option value="">— Select —</option>
                <option v-for="c in cycles" :key="c.id" :value="c.id">{{ c.title }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>Employee *</label>
              <select v-model="form.employee_id">
                <option value="">— Select —</option>
                <option v-for="e in employees" :key="e.id" :value="e.id">{{ e.full_name }}</option>
              </select>
            </div>
            <div class="form-group form-group--full">
              <label>Goal Title *</label>
              <input v-model="form.title" placeholder="e.g. Increase sales revenue by 20%" />
            </div>
            <div class="form-group form-group--full">
              <label>Description</label>
              <textarea v-model="form.description" rows="2" />
            </div>
            <div class="form-group">
              <label>Weight (%)</label>
              <input type="number" v-model.number="form.weight" min="0" max="100" step="1" />
            </div>
            <div class="form-group">
              <label>Target Value</label>
              <input type="number" v-model="form.target_value" step="0.01" />
            </div>
            <div class="form-group">
              <label>Unit</label>
              <input v-model="form.unit" placeholder="e.g. %, units, NGN" />
            </div>
            <div class="form-group">
              <label>Due Date</label>
              <input type="date" v-model="form.due_date" />
            </div>
            <div class="form-group">
              <label>Status</label>
              <select v-model="form.status">
                <option value="draft">Draft</option>
                <option value="approved">Approved</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>
          <div class="modal-actions">
            <button class="btn-outline" @click="showModal = false">Cancel</button>
            <button class="btn-primary" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page { padding: 0 24px 40px; }
.toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.page-title { font-size: 20px; font-weight: 700; color: #1a2a45; margin: 0; }
.filters { display: flex; gap: 10px; margin-bottom: 16px; }
.filter-select { padding: 8px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13px; }
.table-card { background: #fff; border-radius: 10px; border: 1px solid #eaf0fb; overflow: hidden; }
.table-scroll { overflow-x: auto; }
.data-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.data-table th { padding: 12px 14px; text-align: left; background: #f7f9fc; font-weight: 600; color: #556; font-size: 12px; white-space: nowrap; }
.data-table td { padding: 12px 14px; border-top: 1px solid #f0f3fa; vertical-align: top; }
.tbl-empty { text-align: center; color: #999; padding: 40px; }
.goal-title { font-weight: 600; color: #1a2a45; }
.goal-desc  { font-size: 12px; color: #888; margin-top: 2px; }
.actions-cell { white-space: nowrap; }
.pagination { display: flex; align-items: center; gap: 12px; justify-content: center; padding: 12px; }
.pagination button { padding: 5px 12px; border: 1px solid #dde3ef; border-radius: 4px; cursor: pointer; background: #fff; }
.pagination button:disabled { opacity: 0.4; cursor: not-allowed; }
.badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 600; text-transform: capitalize; }
.badge--grey   { background: #f0f3fa; color: #556; }
.badge--green  { background: #d1e7dd; color: #0f5132; }
.badge--blue   { background: #cfe2ff; color: #084298; }
.badge--red    { background: #f8d7da; color: #842029; }
.btn-primary  { padding: 9px 20px; background: #004AAD; color: #fff; border: none; border-radius: 6px; font-size: 13.5px; font-weight: 600; cursor: pointer; }
.btn-outline  { padding: 9px 20px; background: #fff; color: #004AAD; border: 1px solid #004AAD; border-radius: 6px; font-size: 13.5px; cursor: pointer; }
.btn-link     { background: none; border: none; color: #004AAD; font-size: 13px; cursor: pointer; padding: 0 4px; }
.btn-link--red { color: #dc3545; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000; display: flex; align-items: flex-start; justify-content: center; padding-top: 60px; }
.modal-box     { background: #fff; border-radius: 12px; width: 640px; max-width: 95vw; max-height: 85vh; overflow-y: auto; box-shadow: 0 8px 40px rgba(0,0,0,0.18); }
.modal-header  { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid #eaf0fb; }
.modal-header h3 { font-size: 16px; font-weight: 700; color: #1a2a45; margin: 0; }
.modal-close   { background: none; border: none; font-size: 22px; color: #888; cursor: pointer; }
.modal-body    { padding: 20px 24px 24px; }
.modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
.form-grid     { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 18px; }
.form-group    { display: flex; flex-direction: column; gap: 5px; }
.form-group--full { grid-column: 1 / -1; }
.form-group label { font-size: 12.5px; font-weight: 600; color: #556; }
.form-group input, .form-group select, .form-group textarea { padding: 9px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13.5px; color: #1a2a45; resize: vertical; }
.form-error { background: #f8d7da; color: #842029; padding: 10px 14px; border-radius: 6px; font-size: 13px; margin-bottom: 14px; }
</style>
